// SPDX-FileCopyrightText: 2026 Angels' Ware
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState, type ReactNode } from "react";
import { Check, Copy, Terminal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth";
import { api } from "../../api";
import { explainApiError } from "../../lib/explainApiError";
import { Button } from "../ui/Button";
import type { IssuedAPIKey } from "../../types";
import { ErrorNotice } from "../ui/ErrorNotice";
import { ICON } from "../../icons";
import { FEEDBACK } from "../../lib/timing";
import { useEscapeToClose } from "../ui/useEscapeToClose";


type Stage = "confirm" | "reveal";

type ClientID = "claude-desktop" | "claude-code";

// OS drives which config-file path we show — the only OS-dependent bit
// (the JSON snippet is identical and the Claude Code CLI is
// cross-platform). Default is best-effort from the browser.
type OS = "macos" | "windows" | "linux";
const OSES: OS[] = ["macos", "windows", "linux"];
const OS_ICON: Record<OS, string> = {
  macos: "/brands/os-apple.svg",
  windows: "/brands/os-windows.svg",
  linux: "/brands/os-linux.svg",
};

function detectOS(): OS {
  const s = (navigator.userAgent + " " + (navigator.platform || "")).toLowerCase();
  if (s.includes("win")) return "windows";
  if (s.includes("linux") && !s.includes("android")) return "linux";
  return "macos";
}

type ClientDef = {
  id: ClientID;
  labelKey: string;
  vendorKey: string;
  Icon: () => ReactNode;
  instructionsKey: string;
  configPathKeys: Record<OS, string>;
  buildJSON: (env: SnippetEnv) => string;
  buildCLI?: (env: SnippetEnv) => string;
};

type SnippetEnv = { url: string; secret: string };

const CLIENTS: ClientDef[] = [
  {
    id: "claude-desktop",
    labelKey: "connectMcp.clients.claudeDesktop.label",
    vendorKey: "connectMcp.clients.anthropic",
    Icon: ClaudeLogo,
    instructionsKey: "connectMcp.clients.claudeDesktop.instructions",
    configPathKeys: {
      macos: "connectMcp.clients.claudeDesktop.configPath.macos",
      windows: "connectMcp.clients.claudeDesktop.configPath.windows",
      linux: "connectMcp.clients.claudeDesktop.configPath.linux",
    },
    buildJSON: mcpServersJSON,
  },
  {
    id: "claude-code",
    labelKey: "connectMcp.clients.claudeCode.label",
    vendorKey: "connectMcp.clients.anthropic",
    Icon: ClaudeCodeLogo,
    instructionsKey: "connectMcp.clients.claudeCode.instructions",
    configPathKeys: {
      macos: "connectMcp.clients.claudeCode.configPath.macos",
      windows: "connectMcp.clients.claudeCode.configPath.windows",
      linux: "connectMcp.clients.claudeCode.configPath.linux",
    },
    buildJSON: mcpServersJSON,
    buildCLI: ({ url, secret }) =>
      // Single line with double-quoted KEY=VALUE args so the command
      // pastes cleanly into POSIX shells, Windows cmd.exe, AND
      // PowerShell. Avoid backslash line-continuations (POSIX-only; cmd
      // uses `^`, PowerShell uses a backtick) and POSIX single-quoting
      // (cmd.exe doesn't treat `'` as a quote). The trailing `--`
      // separates `claude mcp add` flags from the command it spawns.
      `claude mcp add dazyflow --env "DAZYFLOW_URL=${url}" --env "DAZYFLOW_API_KEY=${secret}" -- dz-mcp`,
  },
];

export function ConnectMcpClientModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { token, me, activeWorkspace } = useAuth();
  const [stage, setStage] = useState<Stage>("confirm");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedAPIKey | null>(null);

  const subject = me?.subject ?? "";
  const workspace = activeWorkspace || me?.workspace || "";
  const canEdit = me?.permissions?.includes("graph:edit") ?? false;

  const create = async () => {
    if (!token) return;
    if (!subject) {
      setError(t("connectMcp.noSubject"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const k = await api.issueMyAPIKey(token, {});
      setIssued(k);
      setStage("reveal");
    } catch (e) {
      setError(explainApiError(e, t));
    } finally {
      setSubmitting(false);
    }
  };

  useEscapeToClose(onClose);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal mcp-connect-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-head">
          <h2>{t("connectMcp.title")}</h2>
        </div>
        {stage === "confirm" ? (
          <ConfirmStage
            subject={subject}
            workspace={workspace}
            canEdit={canEdit}
            submitting={submitting}
            error={error}
            onCancel={onClose}
            onCreate={create}
          />
        ) : (
          <RevealStage issued={issued!} onDone={onClose} />
        )}
      </div>
    </div>
  );
}

function ConfirmStage({
  subject,
  workspace,
  canEdit,
  submitting,
  error,
  onCancel,
  onCreate,
}: {
  subject: string;
  workspace: string;
  canEdit: boolean;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onCreate: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="modal-body">
        {/* The remote connector needs no key: Claude signs in over OAuth and
            the user approves it here, so it leads. */}
        <div className="sf-field">
          <div className="label-row">
            <label>{t("connectMcp.remoteTitle")}</label>
          </div>
          <div className="desc">{t("connectMcp.remoteHelp")}</div>
          <input
            value={`${window.location.origin}/mcp`}
            readOnly
            aria-label={t("connectMcp.remoteUrlLabel")}
            onFocus={(e) => e.currentTarget.select()}
            style={{ fontFamily: "var(--font-mono)" }}
          />
          <CopyButton text={`${window.location.origin}/mcp`} labelKey="connectMcp.copyUrl" />
        </div>
        <h3>{t("connectMcp.localTitle")}</h3>
        <p className="settings-help">
          {t(canEdit ? "connectMcp.introRunEdit" : "connectMcp.introRun")}
        </p>
        <div className="sf-field">
          <div className="label-row">
            <label>{t("connectMcp.subjectLabel")}</label>
          </div>
          <input value={subject} disabled style={{ fontFamily: "var(--font-mono)" }} />
        </div>
        <div className="sf-field">
          <div className="label-row">
            <label>{t("connectMcp.workspaceLabel")}</label>
          </div>
          <input
            value={workspace || t("connectMcp.workspaceAny")}
            disabled
            style={{ fontFamily: "var(--font-mono)" }}
          />
          <div className="desc">{t("connectMcp.scopeDesc")}</div>
        </div>
        {error && (
          <ErrorNotice style={{ marginTop: "var(--space-3)" }}>
{error}
          </ErrorNotice>
        )}
      </div>
      <div className="modal-foot">
        <Button type="button" onClick={onCancel} disabled={submitting}>
          {t("common.cancel")}
        </Button>
        <Button type="button" variant="primary" onClick={onCreate} disabled={submitting}>
          {submitting ? t("connectMcp.creating") : t("connectMcp.create")}
        </Button>
      </div>
    </>
  );
}

function RevealStage({ issued, onDone }: { issued: IssuedAPIKey; onDone: () => void }) {
  const { t } = useTranslation();
  const [activeID, setActiveID] = useState<ClientID>(CLIENTS[0].id);
  const [os, setOS] = useState<OS>(detectOS);
  const env: SnippetEnv = {
    url: window.location.origin,
    secret: issued.secret,
  };
  const active = CLIENTS.find((c) => c.id === activeID) ?? CLIENTS[0];
  const json = active.buildJSON(env);
  const cli = active.buildCLI?.(env);
  return (
    <>
      <div className="modal-body">
        <p className="settings-help">{t("connectMcp.revealHelp")}</p>
        <div className="mcp-client-grid">
          {CLIENTS.map((c) => (
            <Button
              key={c.id}
              type="button"
              className={"mcp-client-card" + (c.id === activeID ? " active" : "")}
              onClick={() => setActiveID(c.id)}
            >
              <span className="mcp-client-icon">
                <c.Icon />
              </span>
              <span className="mcp-client-meta">
                <span className="mcp-client-name">{t(c.labelKey)}</span>
                <span className="mcp-client-vendor">{t(c.vendorKey)}</span>
              </span>
            </Button>
          ))}
        </div>
        <div className="mcp-client-instructions">
          <div className="mcp-os-tabs" role="tablist">
            {OSES.map((o) => (
              <Button
                key={o}
                type="button"
                role="tab"
                aria-selected={o === os}
                className={"mcp-os-tab" + (o === os ? " active" : "")}
                onClick={() => setOS(o)}
              >
                <img
                  src={OS_ICON[o]}
                  alt=""
                  className="mcp-os-icon"
                  width={14}
                  height={14}
                  draggable={false}
                />
                {t(`connectMcp.os.${o}`)}
              </Button>
            ))}
          </div>
          <p>{t(active.instructionsKey)}</p>
          <div className="sf-field">
            <div className="label-row">
              <label>{t("connectMcp.configPathLabel")}</label>
            </div>
            <input
              value={t(active.configPathKeys[os])}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
              style={{ fontFamily: "var(--font-mono)" }}
            />
          </div>
          <div className="sf-field">
            <div className="label-row">
              <label>{t("connectMcp.snippetLabel")}</label>
            </div>
            <pre className="secret-reveal" style={{ whiteSpace: "pre", overflowX: "auto" }}>
              {json}
            </pre>
            <CopyButton text={json} labelKey="connectMcp.copySnippet" />
          </div>
          {cli && (
            <div className="sf-field">
              <div className="label-row">
                <label>
                  <Terminal className="icon-lede" size={ICON.xs} />
                  {t("connectMcp.cliLabel")}
                </label>
              </div>
              <pre className="secret-reveal" style={{ whiteSpace: "pre", overflowX: "auto" }}>
                {cli}
              </pre>
              <CopyButton text={cli} labelKey="connectMcp.copyCli" />
            </div>
          )}
        </div>
        <details style={{ marginTop: "var(--space-3)" }}>
          <summary className="muted" style={{ cursor: "pointer" }}>
            {t("connectMcp.advancedSummary")}
          </summary>
          <div className="sf-field" style={{ marginTop: "var(--space-2)" }}>
            <div className="label-row">
              <label>{t("connectMcp.rawTokenLabel")}</label>
            </div>
            <input
              value={issued.secret}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
              style={{ fontFamily: "var(--font-mono)" }}
            />
            <CopyButton text={issued.secret} labelKey="connectMcp.copyToken" />
          </div>
        </details>
      </div>
      <div className="modal-foot">
        <Button type="button" variant="primary" onClick={onDone}>
          {t("connectMcp.done")}
        </Button>
      </div>
    </>
  );
}

function CopyButton({ text, labelKey }: { text: string; labelKey: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), FEEDBACK.copied);
    } catch {
      /* clipboard blocked — input/pre is selectable for manual copy */
    }
  };
  return (
    <Button type="button" onClick={copy} style={{ marginTop: "var(--space-2)" }}>
      {copied ? (
        <Check size={ICON.xs} />
      ) : (
        <Copy size={ICON.xs} />
      )}
      {copied ? t("common.copied") : t(labelKey)}
    </Button>
  );
}

function mcpServersJSON({ url, secret }: SnippetEnv): string {
  const config = {
    mcpServers: {
      "dazyflow": {
        command: "dz-mcp",
        env: {
          DAZYFLOW_URL: url,
          DAZYFLOW_API_KEY: secret,
        },
      },
    },
  };
  return JSON.stringify(config, null, 2);
}

function ClaudeLogo() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 2 L13.4 9.3 L20.5 7.4 L15.1 12 L20.5 16.6 L13.4 14.7 L12 22 L10.6 14.7 L3.5 16.6 L8.9 12 L3.5 7.4 L10.6 9.3 Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ClaudeCodeLogo() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x="2.5"
        y="4.5"
        width="19"
        height="15"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M6 9 L9 12 L6 15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11 15 L16 15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
